/**
 * /admin/v1/dev/* API 客户端 · W2-9 PR-2 数据工厂 v0
 *
 * 承接：CLAUDE.md §11.5.1 数据工厂 / 场景脚本
 *
 * 注意：本文件请求**绕过 mock 拦截器**——dev 数据工厂的目的是真投产 PG，
 * 不应被 ?mock=1 拦截。具体做法是直接走 axios（共享 baseURL），不挂 fixture。
 */
import { useMutation } from "@tanstack/react-query";

import { apiClient } from "@/lib/api-client";

export interface SeedSessionsRequest {
	count: number;
	merchant_id?: string;
	intent?: string;
	progression_state?:
		| "need_gathering"
		| "recommending"
		| "objection_handling"
		| "closing"
		| "handoff";
}

export interface SeedSessionsResponse {
	created: Array<{
		session_id: string;
		merchant_id: string;
		customer_id: string | null;
		intent: string | null;
		progression_state: string;
	}>;
	bootstrap_merchant_inserted: boolean;
}

export interface SeedTracesRequest {
	count: number;
	session_id?: string;
	merchant_id?: string;
	final_outcome?: "success" | "partial" | "failed" | "handoff";
	total_latency_ms?: number;
}

export interface SeedTracesResponse {
	created: Array<{
		trace_id: string;
		session_id: string;
		merchant_id: string;
		turn_index: number;
		final_outcome: string;
	}>;
	auto_created_session_id: string | null;
}

export interface ScenarioRunRequest {
	name: string;
	yaml_text?: string;
	steps?: Array<{ action: string; params?: Record<string, unknown> }>;
	merchant_id?: string;
}

export interface ScenarioRunResponse {
	name: string;
	total_steps: number;
	succeeded: number;
	failed: number;
	results: Array<{
		step_index: number;
		action: string;
		success: boolean;
		output: Record<string, unknown>;
		error: string | null;
	}>;
}

export interface CleanupResponse {
	sessions_deleted: number;
	traces_deleted: number;
	merchant_id: string;
}

export function useSeedSessions() {
	return useMutation<SeedSessionsResponse, Error, SeedSessionsRequest>({
		mutationFn: async (body) => {
			const { data } = await apiClient.post<SeedSessionsResponse>(
				"/admin/v1/dev/seed/sessions",
				body,
			);
			return data;
		},
	});
}

export function useSeedTraces() {
	return useMutation<SeedTracesResponse, Error, SeedTracesRequest>({
		mutationFn: async (body) => {
			const { data } = await apiClient.post<SeedTracesResponse>("/admin/v1/dev/seed/traces", body);
			return data;
		},
	});
}

export function useRunScenario() {
	return useMutation<ScenarioRunResponse, Error, ScenarioRunRequest>({
		mutationFn: async (body) => {
			const { data } = await apiClient.post<ScenarioRunResponse>(
				"/admin/v1/dev/scenarios/run",
				body,
			);
			return data;
		},
	});
}

export function useCleanup() {
	return useMutation<CleanupResponse, Error, { merchant_id?: string } | undefined>({
		mutationFn: async (params) => {
			const { data } = await apiClient.delete<CleanupResponse>("/admin/v1/dev/seed/cleanup", {
				params,
			});
			return data;
		},
	});
}
